import { useState, useEffect } from "react";
import { useDocPrice } from "@/utils/docPrices";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import { IonSkeletonText } from "@ionic/react";
import { generateResumePreview } from "@/utils/resumePreviewGenerator";
import AppResumeBuilder from "./AppResumeBuilder";
import { useDisabledGenerators, useGeneratorGate } from "@/utils/generatorAvailability";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const RESUME_STORAGE_KEY = "resumeBuilderFormData";

// Built-in AI Resume Builder templates (same set as the builder's picker)
const BUILT_IN_TEMPLATES = [
  { value: "ats",     name: "ATS Optimized",       color: "#2563eb" },
  { value: "modern",  name: "Modern Professional", color: "#34d399" },
  { value: "classic", name: "Classic Executive",   color: "#1e293b" },
];

// Sample resume data used to render the card previews
const RESUME_SAMPLE = {
  personalInfo: {
    fullName: "John Smith",
    email: "john.smith@example.com", phone: "(555) 123-4567",
    location: "New York, NY", linkedin: "linkedin.com/in/johnsmith", website: "",
  },
  professionalSummary: "Operations analyst with 7+ years of experience turning messy data into clear decisions. Led cross-functional projects that cut fulfillment costs 18% while improving on-time delivery.",
  optimizedSkills: {
    technical: ["SQL", "Excel / Power Query", "Tableau", "Python"],
    soft: ["Stakeholder communication", "Process design"],
    other: [],
  },
  optimizedExperience: [
    {
      position: "Senior Operations Analyst", company: "Acme Corporation", location: "New York, NY",
      startDate: "2022-03", endDate: "", current: true,
      bullets: [
        "Built the weekly S&OP dashboard used by 40+ managers, replacing three manual reports",
        "Cut fulfillment costs 18% by redesigning the carrier selection process",
        "Mentored two junior analysts through their first automation projects",
      ],
    },
    {
      position: "Operations Analyst", company: "Hudson Logistics", location: "Brooklyn, NY",
      startDate: "2019-01", endDate: "2022-02", current: false,
      bullets: [
        "Automated daily volume forecasting, saving 10 hours per week",
        "Ran root-cause analysis that reduced mis-ships by 32%",
      ],
    },
  ],
  education: [
    { degree: "B.B.A.", field: "Business Administration", institution: "New York University", gpa: "3.7", graduationDate: "2018-05" },
  ],
  font: "Calibri", sectionLayout: "standard", onePage: false, isPaid: false,
};

export default function AppResumes() {
  const seoPrice = useDocPrice("ai-resume", 9.99); // as the admin prices it
  // Hero preview mode: the marketing homepage embeds this page inside a hero
  // phone — boot straight into the builder modal, hide the page behind it.
  const heroPreview = new URLSearchParams(window.location.search).has("heroPreview");
  const [previews, setPreviews] = useState({});
  const [loadingPreviews, setLoadingPreviews] = useState(true);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);   // the business's own (Templates, filed under Resumes)
  const [builderOpen, setBuilderOpen] = useState(heroPreview);
  const [embedTpl, setEmbedTpl] = useState(null);         // { value, name } when the builder opens on an own design

  // Admin-published custom resume templates join the grid
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/doc-templates?documentType=resume`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setCustomTemplates(d.templates || []); })
      .catch(() => {});
    if (!isUserLoggedIn()) return;
    fetch(`${BACKEND_URL}/api/user/doc-templates?documentType=resume&published=1`, { headers: userHeaders() })
      .then((r) => r.json())
      .then((d) => { if (d && d.success && d.hasAccess) setUserTemplates(d.templates || []); })
      .catch(() => {});
  }, []);

  // Admin can disable the whole builder or individual built-in styles from
  // Site Settings (custom templates are unpublished from the template editor)
  const disabledGenerators = useDisabledGenerators();
  useGeneratorGate(["ai-resume"]); // the builder hidden from this visitor: back to /app
  const templateCards = disabledGenerators.has("ai-resume") ? [] : [
    ...BUILT_IN_TEMPLATES.filter((t) => !disabledGenerators.has(`resume-${t.value}`)),
    ...customTemplates
      .filter((t) => !disabledGenerators.has(`custom:${t.id}`))
      .map((t) => ({ value: `custom:${t.id}`, name: t.name, color: t.badgeColor || "#34d399" })),
    ...userTemplates.map((t) => ({ value: `usercustom:${t.id}`, name: t.name, color: t.badgeColor || "#34d399" })),
  ];

  // Render each template's sample preview as its card image
  useEffect(() => {
    if (heroPreview) return undefined; // hero phone never shows the cards
    let cancelled = false;
    (async () => {
      for (const tpl of templateCards) {
        if (previews[tpl.value]) continue;
        try {
          const img = await generateResumePreview({ ...RESUME_SAMPLE, template: tpl.value });
          if (cancelled) return;
          if (img) setPreviews(prev => ({ ...prev, [tpl.value]: img }));
        } catch (err) {
          console.error(`Resume preview failed for ${tpl.value}:`, err);
        }
      }
      if (!cancelled) setLoadingPreviews(false);
    })();
    return () => { cancelled = true; };
  }, [customTemplates, userTemplates]); // eslint-disable-line

  // Preselect the tapped template, then mount a fresh builder so it picks it
  // up. An own design opens the builder embedded on it, so the choice never
  // overwrites the style the normal builder remembers.
  const openBuilder = (templateValue) => {
    if (String(templateValue).startsWith("usercustom:")) {
      setEmbedTpl({ value: templateValue, name: (templateCards.find((t) => t.value === templateValue) || {}).name || "" });
      setBuilderOpen(true);
      return;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(RESUME_STORAGE_KEY) || "{}");
      localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify({ ...stored, template: templateValue }));
    } catch {}
    setBuilderOpen(true);
  };

  return (
    <AppLayout fillHeight>
      <AppSeo title="AI Resume Builder" description="Build an ATS-optimized professional resume in minutes with AI assistance and instant PDF download." path="/app/resumes" price={seoPrice} appName="MintSlip AI Resume Builder" />
      <div style={{ padding: 10, height: "100%", boxSizing: "border-box", ...(heroPreview ? { display: "none" } : null) }}>
        <div style={{ background: "var(--ion-card-background)", borderRadius: 6, padding: "20px 20px 24px", height: "100%", overflowY: "auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", boxSizing: "border-box" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
            {templateCards.map((tpl, cardIdx) => (
              <div key={tpl.value} className="app-sample-card ion-activatable"
                onClick={() => openBuilder(tpl.value)}
                style={{ animationDelay: `${cardIdx * 60}ms`, cursor: "pointer", borderRadius: 10, border: "1.5px solid var(--app-divider, rgba(0,0,0,0.12))", background: "var(--ion-card-background)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "box-shadow 0.2s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
              >
                <ion-ripple-effect />
                <div style={{ position: "relative", background: "#fff", overflow: "hidden", minHeight: 160 }}>
                  <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: tpl.color, color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.28)" }}>
                    {tpl.name}
                  </div>
                  {previews[tpl.value] ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden", pointerEvents: "none" }}>
                      <img src={previews[tpl.value]} alt={`${tpl.name} sample`} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
                      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: "linear-gradient(to top, rgba(0,0,0,0.28), transparent)" }} />
                    </div>
                  ) : loadingPreviews ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden" }}>
                      <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0, "--background": "rgba(0, 0, 0, 0.085)", "--background-rgb": "18, 18, 18" }} />
                    </div>
                  ) : (
                    <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb" }}>
                      <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>Preview unavailable</span>
                    </div>
                  )}
                </div>
                <div style={{ padding: "8px 16px 12px", textAlign: "center", borderTop: "1px solid var(--app-divider, rgba(0,0,0,0.06))" }}>
                  <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ion-color-primary)" }}>Build with {tpl.name} →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mounted fresh per open so it reads the preselected template */}
      {builderOpen && <AppResumeBuilder isOpen embedTemplate={embedTpl ? embedTpl.value : null} embedTemplateName={embedTpl ? embedTpl.name : ""} onClose={() => { setBuilderOpen(false); setEmbedTpl(null); }} />}
    </AppLayout>
  );
}
